import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { getAuthRegistry } from "../../lib/auth/AuthRegistry";
import { getAccessRegistry } from "../../lib/auth/AccessRegistry";
import { getCompanyRegistry } from "../../lib/company/companyRegistrySingleton";
import { useLogin } from "../../hooks/auth/useLogin";
import { useRegister } from "../../hooks/auth/useRegister";
import { useRefreshToken } from "../../hooks/auth/useRefreshToken";
import type { User } from "../../lib/types";
import type { AuthTokens } from "../../lib/auth/AuthRegistry";
import type { LoginCredentials, RegisterCredentials } from "../../lib/services/authService";
import { configureApi } from "../../lib/apiClientRuntime";
import { apiClient } from "../../lib/apiClient";

// AuthContext shape
interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  login: (credentials: LoginCredentials) => void;
  register: (credentials: RegisterCredentials) => void;
  logout: () => void;
  refresh: (refreshToken: string) => void;
  loading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const registry = getAuthRegistry();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initializing, setInitializing] = useState(true);

  // React Query hooks
  const loginMutation = useLogin();
  const registerMutation = useRegister();
  const refreshMutation = useRefreshToken();

  // Sync registry state to context
  const [user, setUser] = useState<User | null>(registry.getUser());
  const [isAuthenticated, setIsAuthenticated] = useState(registry.isAuthenticated());

  // Session restoration: validate stored token on mount and restore session
  useEffect(() => {
    if (registry.isTokenValid()) {
      // Token exists and is not expired - restore session
      setUser(registry.getUser());
      setIsAuthenticated(true);

      // Configure API base URL for stored region
      const region = registry.getRegion();
      if (region) {
        const regionalBase = `https://api-${region}.boona.app`;
        configureApi(regionalBase);
        // eslint-disable-next-line no-console
        console.info(`[Auth] Restored session, API base: ${regionalBase}`);
      }
    } else if (registry.getTokens().accessToken) {
      // Token exists but is expired - clear it
      // eslint-disable-next-line no-console
      console.info("[Auth] Stored token expired, clearing session");
      registry.clearAuth();
      setUser(null);
      setIsAuthenticated(false);
    }
    setInitializing(false);
  }, []);

  // Listen for registry changes (simple polling, can be improved with events)
  useEffect(() => {
    setUser(registry.getUser());
    setIsAuthenticated(registry.isAuthenticated());
  }, [registry.getUser(), registry.isAuthenticated()]);

  // Login handler
  const login = (credentials: LoginCredentials) => {
    setLoading(true);
    setError(null);
    loginMutation.mutate(credentials, {
      onSuccess: (result) => {
        registry.setAuth(result.user, result.tokens, result.region);
        if (result.region) {
          const regionalBase = `https://api-${result.region}.boona.app`;
          configureApi(regionalBase);
          // eslint-disable-next-line no-console
          console.info(`[Auth] Switched API base to ${regionalBase}`);
        }
        setUser(result.user);
        setIsAuthenticated(true);
        setLoading(false);
        navigate("/"); // Redirect to dashboard after successful login
      },
      onError: (err) => {
        setError(err.message);
        setLoading(false);
      },
    });
  };

  // Register handler
  const register = (credentials: RegisterCredentials) => {
    setLoading(true);
    setError(null);
    registerMutation.mutate(credentials, {
      onSuccess: (result) => {
        registry.setAuth(result.user, result.tokens);
        setUser(result.user);
        setIsAuthenticated(true);
        setLoading(false);
      },
      onError: (err) => {
        setError(err.message);
        setLoading(false);
      },
    });
  };

  // Logout handler
  const logout = async () => {
    try {
      // Call backend to clear the SSO cookie
      await apiClient.delete("/api/v1/auth/sso/cookie", {
        withCredentials: true
      });
    } catch (err) {
      // Continue with logout even if API call fails
      console.warn("[Auth] Failed to clear SSO cookie:", err);
    }

    // Clear all auth-related state to prevent stale data on re-login
    registry.clearAuth();
    getAccessRegistry().clear();           // Clear RBAC permissions
    getCompanyRegistry().clearSettings();  // Clear company settings
    queryClient.clear();                   // Clear React Query cache

    configureApi("");  // Clear API base (will be reconfigured after next SSO login)
    setUser(null);
    setIsAuthenticated(false);

    // Redirect to external login
    window.location.href = "https://id.boona.app/login";
  };

  // Refresh handler
  const refresh = (refreshToken: string) => {
    setLoading(true);
    setError(null);
    refreshMutation.mutate(refreshToken, {
      onSuccess: (tokens: AuthTokens) => {
        // Only update tokens, keep user
        registry.setAuth(registry.getUser()!, tokens);
        setIsAuthenticated(registry.isAuthenticated());
        setLoading(false);
      },
      onError: (err) => {
        setError(err.message);
        logout();
        setLoading(false);
      },
    });
  };

  const value: AuthContextValue = {
    user,
    isAuthenticated,
    login,
    register,
    logout,
    refresh,
    loading: loading || initializing,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
